import type React from "react";
import { useEffect, useState } from "react";
import type { FlowNode, Greeting } from "../api.ts";

// Side panel that edits one block. Lives outside the SVG canvas as
// a fixed-position drawer on the right; the canvas keeps full size
// behind it. All parameter changes flow through `onChange`; deletion
// is a single button at the bottom (disabled for the root block).

const KIND_FR: Record<FlowNode["kind"], string> = {
  start: "Démarrage",
  play: "Diffuser une annonce",
  collect: "Demander un numéro",
  menu: "Menu DTMF",
  ring: "Sonner les agents",
  voicemail: "Répondeur",
  hangup: "Raccrocher",
};

interface Target { id: string; label: string }

type LinkField = "next" | "onTimeout" | "onInvalid" | "onNoAnswer" | "onNoOneOnline" | "branch";

export function Inspector(props: {
  nodeId: string;
  node: FlowNode;
  /** Visible block number (#N) — the block's identity in the UI. */
  seq: number | null;
  isRoot: boolean;
  targets: Target[]; // every other node, for edge-target dropdowns
  greetings: Greeting[];
  onChange: (next: FlowNode) => void;
  /** Enter "pick the target on the canvas" mode for one edge. */
  onStartLink: (field: LinkField, label: string, branchKey?: string) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  // Local mirror of the node so per-keystroke updates don't bombard
  // the parent reducer; we sync up on each field's onBlur / onChange.
  const [draft, setDraft] = useState<FlowNode>(props.node);
  useEffect(() => {
    setDraft(props.node);
  }, [props.node]);

  function commit<P extends Partial<FlowNode>>(patch: P): void {
    const next = { ...draft, ...patch } as FlowNode;
    setDraft(next);
    props.onChange(next);
  }

  // TargetSelect + "pick on canvas" button, shared by every edge field.
  function LinkableTarget(p: {
    value: string;
    field: LinkField;
    linkLabel: string;
    branchKey?: string;
    onChange: (v: string) => void;
  }) {
    return (
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <TargetSelect value={p.value} options={props.targets} onChange={p.onChange} />
        <button
          type="button"
          className="ghost"
          style={{ flexShrink: 0, padding: "2px 8px" }}
          title="Choisir le bloc cible en cliquant dessus dans le canevas"
          onClick={() => props.onStartLink(p.field, p.linkLabel, p.branchKey)}
        >
          🎯
        </button>
      </div>
    );
  }

  return (
    <aside className="flow-inspector">
      <div className="flow-inspector__head">
        <strong>
          {props.seq != null && <span className="flow-inspector__seq">#{props.seq}</span>}
          {KIND_FR[draft.kind]}
        </strong>
        <span className="flex-spacer" />
        <button type="button" className="ghost" onClick={props.onClose} title="Fermer">✕</button>
      </div>

      {draft.kind === "start" && (
        <Field label="Suite">
          <LinkableTarget value={draft.next} field="next" linkLabel="Suite" onChange={(v) => commit({ next: v })} />
        </Field>
      )}

      {draft.kind === "play" && (
        <>
          <Field label="Annonce">
            <GreetingSelect value={draft.greetingId} greetings={props.greetings} onChange={(v) => commit({ greetingId: v })} />
          </Field>
          <Field label="Suite">
            <LinkableTarget value={draft.next} field="next" linkLabel="Suite" onChange={(v) => commit({ next: v })} />
          </Field>
        </>
      )}

      {draft.kind === "collect" && (
        <>
          <Field label="Invite">
            <GreetingSelect value={draft.greetingId} greetings={props.greetings} onChange={(v) => commit({ greetingId: v })} />
          </Field>
          <Field label="Longueur min">
            <input
              type="number"
              min={1}
              max={20}
              value={draft.min}
              onChange={(e) => commit({ min: parseInt(e.target.value, 10) || 1 })}
            />
          </Field>
          <Field label="Longueur max">
            <input
              type="number"
              min={1}
              max={20}
              value={draft.max}
              onChange={(e) => commit({ max: parseInt(e.target.value, 10) || 1 })}
            />
          </Field>
          <Field label="Afficher à l'agent">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={draft.attachToCallerId}
                onChange={(e) => commit({ attachToCallerId: e.target.checked })}
              />
              <span style={{ fontSize: 12, color: "#475569" }}>
                Préfixe le caller-id par « Affaire NNN »
              </span>
            </label>
          </Field>
          <Field label="Suite">
            <LinkableTarget value={draft.next} field="next" linkLabel="Suite" onChange={(v) => commit({ next: v })} />
          </Field>
        </>
      )}

      {draft.kind === "menu" && (
        <>
          <Field label="Invite">
            <GreetingSelect value={draft.greetingId} greetings={props.greetings} onChange={(v) => commit({ greetingId: v })} />
          </Field>
          <Field label="Délai (s)">
            <input
              type="number"
              min={1}
              max={30}
              value={draft.timeoutSec}
              onChange={(e) => commit({ timeoutSec: parseInt(e.target.value, 10) || 5 })}
            />
          </Field>

          <div className="flow-inspector__section">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 13 }}>Choix par chiffre</strong>
              <span className="flex-spacer" />
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  const used = new Set(Object.keys(draft.branches));
                  const candidates = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "*", "#"];
                  const next = candidates.find((d) => !used.has(d));
                  if (next) commit({ branches: { ...draft.branches, [next]: "" } });
                }}
              >+ Ajouter</button>
            </div>
            {Object.entries(draft.branches)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([digit, target]) => (
                <div key={digit} className="flow-inspector__branch-row">
                  <code style={{ fontWeight: 700, minWidth: "1.5rem" }}>{digit}</code>
                  <span style={{ color: "#94a3b8" }}>→</span>
                  <TargetSelect
                    value={target}
                    options={props.targets}
                    onChange={(v) => commit({ branches: { ...draft.branches, [digit]: v } })}
                  />
                  <button
                    type="button"
                    className="ghost"
                    title="Choisir le bloc cible en cliquant dessus dans le canevas"
                    onClick={() => props.onStartLink("branch", `Choix ${digit}`, digit)}
                  >🎯</button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      const next = { ...draft.branches };
                      delete next[digit];
                      commit({ branches: next });
                    }}
                  >✕</button>
                </div>
              ))}
            {Object.keys(draft.branches).length === 0 && (
              <div className="muted" style={{ fontSize: 12 }}>Ajoutez au moins un choix.</div>
            )}
          </div>

          <Field label="Si aucun chiffre">
            <LinkableTarget value={draft.onTimeout} field="onTimeout" linkLabel="Si aucun chiffre" onChange={(v) => commit({ onTimeout: v })} />
          </Field>
          <Field label="Si invalide">
            <LinkableTarget value={draft.onInvalid} field="onInvalid" linkLabel="Si invalide" onChange={(v) => commit({ onInvalid: v })} />
          </Field>
        </>
      )}

      {draft.kind === "ring" && (
        <>
          <Field label="Sonnerie par agent (s)">
            <input
              type="number"
              min={1}
              max={60}
              value={draft.timeoutSec}
              onChange={(e) => commit({ timeoutSec: parseInt(e.target.value, 10) || 6 })}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              Les agents disponibles sonnent l'un après l'autre ; passé ce
              délai sans réponse, l'appel bascule vers l'agent suivant.
            </span>
          </Field>
          <Field label="Si décroché">
            <span className="muted" style={{ fontSize: 12 }}>
              Conversation établie (rien à configurer)
            </span>
          </Field>
          <Field label="Si sans réponse">
            <LinkableTarget value={draft.onNoAnswer} field="onNoAnswer" linkLabel="Si sans réponse" onChange={(v) => commit({ onNoAnswer: v })} />
          </Field>
          <Field label="Si personne en ligne">
            <LinkableTarget value={draft.onNoOneOnline} field="onNoOneOnline" linkLabel="Si personne en ligne" onChange={(v) => commit({ onNoOneOnline: v })} />
          </Field>
        </>
      )}

      {draft.kind === "voicemail" && (
        <>
          <Field label="Annonce avant le bip">
            <GreetingSelect value={draft.greetingId} greetings={props.greetings} onChange={(v) => commit({ greetingId: v })} />
          </Field>
          <Field label="Mode">
            <select
              value={draft.silent ? "silent" : "default"}
              onChange={(e) => commit({ silent: e.target.value === "silent" })}
            >
              <option value="default">Avec message Asterisk</option>
              <option value="silent">Silencieux — bip + enregistrement</option>
            </select>
          </Field>
        </>
      )}

      {draft.kind === "hangup" && (
        <p className="muted">Termine l'appel proprement.</p>
      )}

      <div className="flow-inspector__actions">
        {!props.isRoot && (
          <button type="button" className="ghost" style={{ color: "#ef4444" }} onClick={props.onDelete}>
            🗑 Supprimer ce bloc
          </button>
        )}
        <span className="flex-spacer" />
        <button type="button" className="primary" onClick={props.onClose}>OK</button>
      </div>
    </aside>
  );
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flow-inspector__field">
      <label>{props.label}</label>
      <div>{props.children}</div>
    </div>
  );
}

function TargetSelect(props: {
  value: string;
  options: Target[];
  onChange: (v: string) => void;
}) {
  return (
    <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
      <option value="">— Choisir —</option>
      {props.options.map((o) => (
        <option key={o.id} value={o.id}>{o.label}</option>
      ))}
    </select>
  );
}

function GreetingSelect(props: {
  value: string | null;
  greetings: Greeting[];
  onChange: (v: string | null) => void;
}) {
  return (
    <select
      value={props.value ?? ""}
      onChange={(e) => props.onChange(e.target.value || null)}
    >
      <option value="">— Aucune —</option>
      {props.greetings.map((g) => (
        <option key={g.id} value={g.id}>{g.name}</option>
      ))}
    </select>
  );
}
